import InputTextField from "components/forms/InputTextField";
import React, { FC, useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";

import Select from "react-select";
import { useTranslation } from "react-i18next";
import { ValidateConstant } from "constant/dashboardConstant";
import { useAuthValidationRules } from "helpers/validation-schema/AuthValidationRules";

import { object } from "yup";
import ErrorMessageBox from "components/base/ErrorMessageBox";
import { SelectionOption } from "helpers/models/auth_models";
import CardBottomSubmitBottom from "components/forms/CardBottomSubmitBottom";
import { useSearchParams } from "react-router-dom";
import { KTIcon } from "helpers";
import apiInterceptor from "services/apiInterceptor";
import { useCommonContext } from "hooks‬/CommonContext";
import { subscriptionApiEndpoint } from "constant/apiConstants";
import { useAuth } from "hooks‬/Auth";
interface PropsType {
  showModal: boolean;
  closeModal: () => void;
  fetchAPI: any;
  roles: any[];
  selectedRole: any;
  isCloneRole: boolean;
  isUpdateRole: boolean;
}
const CreateNewRole: FC<PropsType> = ({
  showModal,
  closeModal,
  fetchAPI,
  roles,
  selectedRole,
  isCloneRole,
  isUpdateRole,
}) => {
  const { t } = useTranslation();
  const validationRules = useAuthValidationRules();
  const { fetchSubcriptionDetail } = useAuth();
  const { showSuccessToast, showErrorToast } = useCommonContext();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const productGuid = searchParams.get("product");
  const [privileges, setPrivileges] = useState<
    {
      guid: string;
      name: string;
      displayValue: string;
    }[]
  >([]);

  const [objectOrAction, setObjectOrAction] = useState<any[]>([]);
  const [privilegeLoader, setPrivilegeLoader] = useState<boolean>(true);
  const [objectOrActionLoader, setObjectOrActionLoader] =
    useState<boolean>(false);
  // Form States
  const [rolePrivilegesPayload, setRolePrivilegesPayload] = useState<any[]>([]);
  const [roleName, setRoleName] = useState("");
  const [typeForCreateNewRole, setTypeForCreateNewRole] = useState<string>(
    ValidateConstant.MANUAL
  );

  const [rolesOptions, setRolesOptions] = useState<SelectionOption[]>([]);
  const [roleNameErrors, setRoleNameErrors] = useState<string>("");
  const [requestErrors, setRequestErrors] = useState<string>("");
  useEffect(() => {
    if (showModal) {
      setRoleNameErrors("");
      setRequestErrors("");
    }
  }, [showModal]);
  // Form Handlers
  const applyRolePrivileges = useCallback(
    (roleGuid: string) => {
      const role = roles.find((r) => r.guid === roleGuid);
      if (!role) return;

      const privilegeMap: Record<string, string> = {};
      role.userRolePrivileges.forEach((urp: any) => {
        if (urp.objectOrAction?.guid && urp.privilege?.guid) {
          privilegeMap[urp.objectOrAction.guid] = urp.privilege.guid;
        }
      });

      setRolePrivilegesPayload((prev) =>
        prev.map((item) => ({
          ...item,
          privilegeGuid:
            privilegeMap[item.objectOrActionGuid] ?? item.privilegeGuid,
        }))
      );
    },
    [roles]
  );

  useEffect(() => {
    if (privileges?.length > 0 && objectOrAction?.length > 0) {
      const noneGuid = privileges.find((p) => p.displayValue === "None")?.guid;
      const result = objectOrAction.map((obj, index) => ({
        objectOrActionGuid: obj.guid,
        privilegeGuid: noneGuid,
      }));
      setRolePrivilegesPayload(result);

      if ((isCloneRole || isUpdateRole) && selectedRole.value.length > 0) {
        setRoleName(selectedRole.label);
        setTypeForCreateNewRole(ValidateConstant.CLONE);
        applyRolePrivileges(selectedRole.value);
      } else {
        setRoleName("");
        setTypeForCreateNewRole(ValidateConstant.MANUAL);
      }
    }
  }, [
    privileges,
    objectOrAction,
    selectedRole,
    applyRolePrivileges,
    isCloneRole,
    isUpdateRole,
  ]);

  const fetchPrivilegesHandler = async () => {
    setObjectOrActionLoader(true);
    await apiInterceptor
      .get(
        `${subscriptionApiEndpoint.ENDPOINT_ALL_PRODUCTS}/${productGuid}/object-or-actions`
      )
      .then((response) => {
        setObjectOrAction(response.data.data);
        setObjectOrActionLoader(false);
      })
      .catch((error) => {
        setObjectOrActionLoader(false);
      });
  };

  useEffect(() => {
    if (showModal) {
      setPrivilegeLoader(true);
      setObjectOrActionLoader(true);
      fetchPrivilegesHandler();
      apiInterceptor
        .get(`${subscriptionApiEndpoint.ENDPOINT_ROLE_PRIVILEGE}`)
        .then((response) => {
          setPrivilegeLoader(false);
          setPrivileges(response.data.data);
        })
        .catch(() => {
          setPrivilegeLoader(false);
        });
    }
    // eslint-disable-next-line
  }, [productGuid, showModal]);

  useEffect(() => {
    if (roles.length > 0) {
      setRolesOptions(
        roles.map((role) => ({
          label: role.displayName,
          value: role.guid,
        }))
      );
    }
  }, [roles]);

  // Check of checkbox is checked or not
  const findRolePrivilegesWithGuid = (guid: string): any => {
    try {
      const current = rolePrivilegesPayload.find(
        (privilege) => privilege.objectOrActionGuid === guid
      );

      return privileges.find(
        (privilege) => privilege.guid === current?.privilegeGuid
      );
    } catch (error: any) {
      console.error(error);
    }
  };

  const findDeletePrivilege = (guid: string): boolean => {
    const currentPrivilege = findRolePrivilegesWithGuid(guid);

    if (currentPrivilege?.displayValue) {
      return currentPrivilege.displayValue === ValidateConstant.DELETE;
    } else {
      return false;
    }
  };

  const findCreatePrivilege = (guid: string): boolean => {
    const currentPrivilege = findRolePrivilegesWithGuid(guid);

    if (currentPrivilege?.displayValue) {
      return (
        currentPrivilege.displayValue === ValidateConstant.DELETE ||
        currentPrivilege.displayValue === ValidateConstant.CREATE
      );
    } else {
      return false;
    }
  };

  const findModifyPrivilege = (guid: string): boolean => {
    const currentPrivilege = findRolePrivilegesWithGuid(guid);

    if (currentPrivilege?.displayValue) {
      return (
        currentPrivilege.displayValue === ValidateConstant.DELETE ||
        currentPrivilege.displayValue === ValidateConstant.CREATE ||
        currentPrivilege.displayValue === ValidateConstant.MODIFY
      );
    } else {
      return false;
    }
  };

  const findReadPrivilege = (guid: string): boolean => {
    const currentPrivilege = findRolePrivilegesWithGuid(guid);

    if (currentPrivilege?.displayValue) {
      return (
        currentPrivilege.displayValue === ValidateConstant.DELETE ||
        currentPrivilege.displayValue === ValidateConstant.CREATE ||
        currentPrivilege.displayValue === ValidateConstant.MODIFY ||
        currentPrivilege.displayValue === ValidateConstant.READ
      );
    } else {
      return false;
    }
  };

  // Setter function to update role privileges
  const commonPrivilegeSetter = (
    objectOrActionGuid: string,
    privilegeGuid: string
  ) => {
    setRolePrivilegesPayload((prev) =>
      prev.map((item) =>
        item.objectOrActionGuid === objectOrActionGuid
          ? { ...item, privilegeGuid }
          : item
      )
    );
  };

  const readChecker = (
    e: any,
    objectOrActionGuid: string,
    privilegeGuid: string
  ) => {
    const isChecked = e.target.checked;
    if (isChecked) {
      commonPrivilegeSetter(objectOrActionGuid, privilegeGuid);
    } else {
      commonPrivilegeSetter(
        objectOrActionGuid,
        privileges.find((obj) => obj.displayValue === ValidateConstant.NONE)
          ?.guid ?? ""
      );
    }
  };

  const modifyChecker = (
    e: any,
    objectOrActionGuid: string,
    privilegeGuid: string
  ) => {
    const isChecked = e.target.checked;

    if (isChecked) {
      commonPrivilegeSetter(objectOrActionGuid, privilegeGuid);
    } else {
      commonPrivilegeSetter(
        objectOrActionGuid,
        privileges.find((obj) => obj.displayValue === ValidateConstant.READ)
          ?.guid ?? ""
      );
    }
  };

  const deleteChecker = (
    e: any,
    objectOrActionGuid: string,
    privilegeGuid: string
  ) => {
    const isChecked = e.target.checked;

    if (isChecked) {
      commonPrivilegeSetter(objectOrActionGuid, privilegeGuid);
    } else {
      commonPrivilegeSetter(
        objectOrActionGuid,
        privileges.find((obj) => obj.displayValue === ValidateConstant.CREATE)
          ?.guid ?? ""
      );
    }
  };

  const createChecker = (
    e: any,
    objectOrActionGuid: string,
    privilegeGuid: string
  ) => {
    const isChecked = e.target.checked;
    if (isChecked) {
      commonPrivilegeSetter(objectOrActionGuid, privilegeGuid);
    } else {
      commonPrivilegeSetter(
        objectOrActionGuid,
        privileges.find((obj) => obj.displayValue === ValidateConstant.MODIFY)
          ?.guid ?? ""
      );
    }
  };

  const Schema = object().shape({
    roleName: validationRules.roleNameValidationSchema,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setRoleName(value);
    Schema.validateAt(name, { roleName: value })
      .then(() => {
        setRoleNameErrors("");
      })
      .catch((error) => {
        setRoleNameErrors(error.message);
      });
  };

  const handleSubmitForm = async () => {
    try {
      await Schema.validate({
        roleName: roleName,
      });

      setRoleNameErrors("");
    } catch (error: any) {
      if (error.path === "roleName") {
        setRoleNameErrors(error.message);
      }
    }
    try {
      await Schema.validate({
        roleName: roleName,
      });
      setIsSubmitting(true);
      setRoleNameErrors("");
      if (isUpdateRole) {
        const role = roles.find((r) => r.guid === selectedRole.value);
        const privilegeMap: Record<string, string> = {};

        role.userRolePrivileges.forEach((urp: any) => {
          if (urp.objectOrAction?.guid && urp.privilege?.guid) {
            privilegeMap[urp.objectOrAction.guid] = urp.guid;
          }
        });

        const updatedPayload = rolePrivilegesPayload.map((item) => ({
          privilegeGuid: item.privilegeGuid,
          guid:
            privilegeMap[item.objectOrActionGuid] ?? item.objectOrActionGuid,
        }));

        await apiInterceptor
          .put(`${subscriptionApiEndpoint.ENDPOINT_USER_ROLE}/${role.guid}`, {
            roleName: roleName.trim(),
            rolePrivileges: updatedPayload,
          })
          .then((response: any) => {
            if (response.data.status === "ERROR") {
              setRequestErrors(response.data.statusDesc);
            } else {
              fetchAPI();
              showSuccessToast("Role updated successfully");
              closeModal();
            }
            setIsSubmitting(false);
          })
          .catch(() => {
            showErrorToast("Error while creating role, please try again later");
          });
      } else {
        await apiInterceptor
          .post(subscriptionApiEndpoint.ENDPOINT_USER_ROLE, {
            productTypeGuid: productGuid,
            roleName: roleName.trim(),
            rolePrivileges: rolePrivilegesPayload,
          })
          .then((response: any) => {
            if (response.data.status === "ERROR") {
              setRequestErrors(response.data.statusDesc);
            } else {
              fetchAPI();
              showSuccessToast("Role created successfully");
              fetchSubcriptionDetail();
              closeModal();
            }
            setIsSubmitting(false);
          })
          .catch(() => {
            showErrorToast("Error while creating role, please try again later");
          });
      }
    } catch (error: any) {
      if (error.path === "roleName") {
        setRoleNameErrors(error.message);
      }
    }
  };
  const element: any =
    objectOrActionLoader || privilegeLoader ? (
      <div className="d-flex justify-content-center align-items-center my-15">
        <div
          className="spinner-border spinner-border-sm align-middle ms-2"
          style={{ width: "100px", height: "100px" }}
        >
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    ) : (
      <div className="fs-6  text-muted">{t("messages:NO.PRIVILEGE.FOUND")}</div>
    );

  return (
    <Modal size="lg" show={showModal} onHide={closeModal} centered>
      <Modal.Body className="p-0">
        <div className="d-flex justify-content-between align-items-center mb-4 border-bottom p-8">
          <div className="fs-24px  fw-medium ">
            {isUpdateRole ? "Update Role" : "Add Role"}
          </div>
          <div>
            <button onClick={closeModal} className="btn p-0 bg-transparent  ">
              <KTIcon iconName="cross" className="fs-30px text-gray-800" />
            </button>
          </div>
        </div>
        <form>
          <div className="border-bottom p-10 py-4">
            {requestErrors?.length > 0 && (
              <ErrorMessageBox massage={requestErrors} />
            )}
            <div className="row  fs-6  ">
              <div className="col-4 mb-5 fs-6 text-gray-800 fw-semibold d-flex align-items-center">
                {t("ROLE.NAME")}
              </div>
              <div className="col-6">
                <InputTextField
                  placeholder={t("ROLE.NAME")}
                  id="roleName"
                  name="roleName"
                  type="text"
                  value={roleName}
                  onBlur={(e) => {
                    handleChange(e);
                  }}
                  onChange={(e) => {
                    handleChange(e);
                  }}
                  error={Boolean(roleNameErrors)}
                  helperText={roleNameErrors}
                  isSolidField={true}
                />
              </div>
            </div>
          </div>
          <div className="  p-10">
            <div className="text-gray-800 fs-24px d-flex mb-10 ">
              {t("PRIVILEGE")}
            </div>
            {!isUpdateRole && (
              <>
                <div className="row my-4  fs-6  ">
                  <div className="col-4 mb-4 fs-6 text-gray-800 fw-semibold d-flex align-items-center">
                    {t("Create role by")}
                  </div>
                  <div className="col-6 d-flex justify-content-start align-content-center">
                    <input
                      type="radio"
                      name="typeForCreateNewRole"
                      value={ValidateConstant.CLONE}
                      checked={typeForCreateNewRole === ValidateConstant.CLONE}
                      onChange={() => {
                        setTypeForCreateNewRole(ValidateConstant.CLONE);
                      }}
                      className="me-5 type-create-role"
                    />{" "}
                    {t("CLONE")}
                    <input
                      type="radio"
                      id="mj-3"
                      name="typeForCreateNewRole"
                      checked={typeForCreateNewRole === ValidateConstant.MANUAL}
                      onChange={() => {
                        setTypeForCreateNewRole(ValidateConstant.MANUAL);
                      }}
                      value="N"
                      className="mx-5 type-create-role"
                    />{" "}
                    {t("MANUAL")}
                  </div>
                </div>
                {typeForCreateNewRole === ValidateConstant.CLONE && (
                  <div className="row my-4 mb-10 fs-6  ">
                    <div className="col-4  fs-6 text-gray-800 fw-semibold d-flex align-items-center">
                      Clone Role
                    </div>

                    <div className="col-6">
                      <Select
                        className="react-select-styled react-select-solid react-select-lg"
                        classNamePrefix="react-select"
                        options={rolesOptions}
                        placeholder="Select an option"
                        onChange={(e) => applyRolePrivileges(e?.value ?? "")}
                        defaultValue={selectedRole}
                      />
                    </div>
                  </div>
                )}{" "}
              </>
            )}
            {objectOrAction.length > 0 && privileges.length > 0 ? (
              <>
                {objectOrAction?.map((item: any) => {
                  return (
                    <div key={item.guid} className="row my-4  fs-6  ">
                      <div className="col-4 mb-4 fs-6 text-gray-800 fw-semibold d-flex   align-items-center">
                        {item.displayValue}
                      </div>
                      <div className="col-8">
                        <div className="d-flex">
                          <label className="form-check form-check-sm form-check-custom form-check-solid me-5 me-lg-20">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              name={item.displayValue}
                              checked={
                                item.guid ? findReadPrivilege(item.guid) : false
                              }
                              onChange={(e) => {
                                readChecker(
                                  e,
                                  item.guid,
                                  privileges.find(
                                    (action: any) =>
                                      action.displayValue === "Read"
                                  )?.guid ?? ""
                                );
                              }}
                            />
                            <span className="form-check-label text-gray-800 fw-medium">
                              {t("READ")}
                            </span>
                          </label>
                          <label className="form-check form-check-sm form-check-custom form-check-solid me-5 me-lg-20">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              name={item.displayValue}
                              checked={
                                item.guid
                                  ? findModifyPrivilege(item.guid)
                                  : false
                              }
                              onChange={(e) => {
                                modifyChecker(
                                  e,

                                  item.guid,
                                  privileges.find(
                                    (action: any) =>
                                      action.displayValue === "Modify"
                                  )?.guid ?? ""
                                );
                              }}
                            />
                            <span className="form-check-label text-gray-800 fw-medium">
                              {t("MODIFY")}
                            </span>
                          </label>

                          <label className="form-check form-check-sm form-check-custom form-check-solid me-5 me-lg-20">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              name={item.displayValue}
                              checked={
                                item.guid
                                  ? findCreatePrivilege(item.guid)
                                  : false
                              }
                              onChange={(e) => {
                                createChecker(
                                  e,

                                  item.guid,
                                  privileges.find(
                                    (action: any) =>
                                      action.displayValue === "Create"
                                  )?.guid ?? ""
                                );
                              }}
                            />
                            <span className="form-check-label text-gray-800 fw-medium">
                              {t("CREATE")}
                            </span>
                          </label>

                          <label className="form-check form-check-sm form-check-custom form-check-solid me-5 me-lg-20">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              name={item.displayValue}
                              checked={
                                item.guid
                                  ? findDeletePrivilege(item.guid)
                                  : false
                              }
                              onChange={(e) => {
                                deleteChecker(
                                  e,
                                  item.guid,
                                  privileges.find(
                                    (action: any) =>
                                      action.displayValue === "Delete"
                                  )?.guid ?? ""
                                );
                              }}
                            />
                            <span className="form-check-label text-gray-800 fw-medium">
                              {t("DELETE")}
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              element
            )}
            <CardBottomSubmitBottom
              label={isUpdateRole ? "Update Role" : t("Add Role")}
              isError={Boolean(roleNameErrors) || isSubmitting}
              handleSubmit={() => handleSubmitForm()}
            />
          </div>
        </form>{" "}
      </Modal.Body>
    </Modal>
  );
};

export default CreateNewRole;
